// Blocks the Member Dashboard for members (typically Google-authed) whose profile
// is missing required KELTA fields. Shown as a full-screen dialog until submitted.
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DISTRICTS, LOCAL_BODY_TYPES } from "@/data/static";
import { GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK, BLOCK_PANCHAYATS_BY_DISTRICT, MUNICIPALITIES_BY_DISTRICT, CORPORATIONS_BY_DISTRICT, DISTRICT_PANCHAYATS_BY_DISTRICT } from "@/data/localBodies";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { UserCircle2 } from "lucide-react";
import DatePicker from "@/components/registers/DatePicker";

export default function ProfileCompletionGate({ user, onDone }) {
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    mobile: user?.mobile || "",
    district: user?.district || "Thiruvananthapuram",
    local_body_type: user?.local_body_type || "Grama Panchayat",
    local_body_name: user?.local_body_name || "",
    block_panchayat: user?.block_panchayat || "",
    designation: user?.designation || "",
    department: user?.department || "",
    dob: user?.dob || "",
    blood_group: user?.blood_group || "",
    educational_qualification: user?.educational_qualification || "",
    employment_type: user?.employment_type || "",
    date_of_joining: user?.date_of_joining || "",
    employee_id: user?.employee_id || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your full name");
    if (!form.mobile.trim() || form.mobile.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit mobile number");
    if (!form.local_body_name.trim()) return toast.error("Select your Local Body");
    if (form.local_body_type === "Grama Panchayat" && !form.block_panchayat) return toast.error("Please select the Block Panchayat");
    try {
      setBusy(true);
      await api.patch("/auth/complete-profile", form);
      toast.success("Profile completed — welcome to KELTA!");
      await refresh();
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue/5 via-background to-brand-orange/5 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <UserCircle2 className="w-6 h-6 text-brand-blue" />
            <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">
              One-time setup
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="complete-profile-heading">
            Complete Your KELTA Profile
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            We need a few more details before you can access your membership dashboard.
            You signed in with Google — please confirm your official Local Body posting details below.
          </p>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Full Name (as on service record) <span className="text-destructive">*</span></Label>
              <Input
                value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                data-testid="cp-fullname"
                placeholder="e.g. Rahul S Nair"
              />
            </div>
            <div>
              <Label>Mobile Number <span className="text-destructive">*</span></Label>
              <Input
                type="tel"
                value={form.mobile}
                onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                data-testid="cp-mobile"
                placeholder="+91 98xxx xxxxx"
              />
            </div>
            <div>
              <Label>District <span className="text-destructive">*</span></Label>
              <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v, block_panchayat: "", local_body_name: "" }))}>
                <SelectTrigger data-testid="cp-district"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISTRICTS.map(d => <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Local Body Type <span className="text-destructive">*</span></Label>
              <Select value={form.local_body_type} onValueChange={v => setForm(f => ({ ...f, local_body_type: v, block_panchayat: "", local_body_name: "" }))}>
                <SelectTrigger data-testid="cp-lbtype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCAL_BODY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.local_body_type === "Grama Panchayat" ? (
              <>
                <div>
                  <Label>Block Panchayat <span className="text-destructive">*</span></Label>
                  <Select
                    value={form.block_panchayat}
                    onValueChange={v => setForm(f => ({ ...f, block_panchayat: v, local_body_name: "" }))}
                  >
                    <SelectTrigger data-testid="cp-block"><SelectValue placeholder="Select Block" /></SelectTrigger>
                    <SelectContent>
                      {(BLOCK_PANCHAYATS_BY_DISTRICT[form.district] || []).map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Grama Panchayat <span className="text-destructive">*</span></Label>
                  <Select
                    value={form.local_body_name}
                    onValueChange={v => setForm(f => ({ ...f, local_body_name: v }))}
                    disabled={!form.block_panchayat}
                  >
                    <SelectTrigger data-testid="cp-lbname"><SelectValue placeholder={form.block_panchayat ? "Select Grama Panchayat" : "Select Block first"} /></SelectTrigger>
                    <SelectContent>
                      {((GRAMA_PANCHAYATS_BY_DISTRICT_BLOCK[form.district] || {})[form.block_panchayat] || []).map(gp => (
                        <SelectItem key={gp} value={gp}>{gp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div>
                <Label>Local Body Name <span className="text-destructive">*</span></Label>
                {form.local_body_type === "Block Panchayat" ? (
                  <Select value={form.local_body_name} onValueChange={v => setForm(f => ({ ...f, local_body_name: v }))}>
                    <SelectTrigger data-testid="cp-lbname"><SelectValue placeholder="Select Block Panchayat" /></SelectTrigger>
                    <SelectContent>
                      {(BLOCK_PANCHAYATS_BY_DISTRICT[form.district] || []).map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.local_body_type === "Municipality" ? (
                  <Select value={form.local_body_name} onValueChange={v => setForm(f => ({ ...f, local_body_name: v }))}>
                    <SelectTrigger data-testid="cp-lbname"><SelectValue placeholder="Select Municipality" /></SelectTrigger>
                    <SelectContent>
                      {(MUNICIPALITIES_BY_DISTRICT[form.district] || []).map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.local_body_type === "Corporation" ? (
                  <Select value={form.local_body_name} onValueChange={v => setForm(f => ({ ...f, local_body_name: v }))}>
                    <SelectTrigger data-testid="cp-lbname"><SelectValue placeholder="Select Corporation" /></SelectTrigger>
                    <SelectContent>
                      {(CORPORATIONS_BY_DISTRICT[form.district] || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : form.local_body_type === "District Panchayat" ? (
                  <Select value={form.local_body_name} onValueChange={v => setForm(f => ({ ...f, local_body_name: v }))}>
                    <SelectTrigger data-testid="cp-lbname"><SelectValue placeholder="Select District Panchayat" /></SelectTrigger>
                    <SelectContent>
                      {(DISTRICT_PANCHAYATS_BY_DISTRICT[form.district] || []).map(dp => (
                        <SelectItem key={dp} value={dp}>{dp}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.local_body_name}
                    onChange={e => setForm(f => ({ ...f, local_body_name: e.target.value }))}
                    data-testid="cp-lbname"
                    placeholder="e.g. Kochi Municipal Corporation"
                  />
                )}
              </div>
            )}
            <div>
              <Label>Designation <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                value={form.designation}
                onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                data-testid="cp-designation"
                placeholder="e.g. Technical Assistant Gr. II"
              />
            </div>
            <div>
              <Label>Department <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                data-testid="cp-department"
                placeholder="e.g. Engineering"
              />
            </div>

            <div className="sm:col-span-2 border-t border-border pt-4 mt-2">
              <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Membership Details</div>
              <p className="text-xs text-muted-foreground mt-0.5">Required for your official KELTA membership certificate.</p>
            </div>
            <div>
              <Label>Employee ID</Label>
              <Input
                value={form.employee_id}
                onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}
                data-testid="cp-employee-id"
                placeholder="e.g. EMP-88421"
              />
            </div>
            <div>
              <Label>Date of Joining</Label>
              <DatePicker
                value={form.date_of_joining ? String(form.date_of_joining).slice(0, 10) : ""}
                onChange={v => setForm(f => ({ ...f, date_of_joining: v }))}
                testid="cp-doj"
              />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <DatePicker
                value={form.dob ? String(form.dob).slice(0, 10) : ""}
                onChange={v => setForm(f => ({ ...f, dob: v }))}
                testid="cp-dob"
              />
            </div>
            <div>
              <Label>Blood Group</Label>
              <Select value={form.blood_group} onValueChange={v => setForm(f => ({ ...f, blood_group: v }))}>
                <SelectTrigger data-testid="cp-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Educational Qualification</Label>
              <Input
                value={form.educational_qualification}
                onChange={e => setForm(f => ({ ...f, educational_qualification: e.target.value }))}
                data-testid="cp-education"
                placeholder="e.g. B.Tech Civil Engineering"
              />
            </div>
            <div>
              <Label>Employment Type</Label>
              <Select value={form.employment_type} onValueChange={v => setForm(f => ({ ...f, employment_type: v }))}>
                <SelectTrigger data-testid="cp-employment-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["Permanent", "Contract", "Deputation", "Provisional", "Daily Wages"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 flex justify-end pt-2">
              <Button
                type="submit"
                disabled={busy}
                className="bg-brand-blue text-white hover:bg-brand-blue/90 h-11 px-6"
                data-testid="cp-submit"
              >
                {busy ? "Saving…" : "Save & Continue"}
              </Button>
            </div>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
